import type { GameOptionDefinition, GameOptionsContext, GameOptionsValue, GameSavedOptions } from './types/options'

export interface ValidateOptionsResult {
  ok: boolean
  resolved?: GameOptionsValue
  errors?: string[]
}

export function playerCountBucket(playerCount: number | undefined): number | undefined {
  if (typeof playerCount !== 'number' || !Number.isFinite(playerCount)) return undefined
  if (playerCount <= 1) return 1
  if (playerCount === 2) return 2
  if (playerCount === 3) return 3
  return 4 // 4+
}

function appliesForPlayerCount(def: GameOptionDefinition, ctx: GameOptionsContext): boolean {
  const allowed = def.appliesWhen?.playerCounts
  if (!allowed || allowed.length === 0) return true
  const count = playerCountBucket(ctx.playerCount)
  if (typeof count !== 'number') return true
  return allowed.includes(count)
}

function defaultFor(def: GameOptionDefinition, ctx: GameOptionsContext): unknown {
  const count = playerCountBucket(ctx.playerCount)
  if (typeof count === 'number' && def.defaultByPlayerCount && def.defaultByPlayerCount[count] !== undefined) {
    return def.defaultByPlayerCount[count]
  }
  return def.defaultValue
}

function validateSingle(def: GameOptionDefinition, value: unknown): { ok: true; value: unknown } | { ok: false; error: string } {
  switch (def.type) {
    case 'boolean': {
      if (typeof value === 'boolean') return { ok: true, value }
      return { ok: false, error: `${def.id} must be boolean` }
    }
    case 'string': {
      if (typeof value === 'string') return { ok: true, value }
      return { ok: false, error: `${def.id} must be string` }
    }
    case 'number': {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return { ok: false, error: `${def.id} must be number` }
      }
      if (typeof def.min === 'number' && value < def.min) {
        return { ok: false, error: `${def.id} must be >= ${def.min}` }
      }
      if (typeof def.max === 'number' && value > def.max) {
        return { ok: false, error: `${def.id} must be <= ${def.max}` }
      }
      return { ok: true, value }
    }
    case 'enum': {
      if (typeof value !== 'string') return { ok: false, error: `${def.id} must be string` }
      const allowed = new Set((def.choices || []).map((c) => c.value))
      if (!allowed.has(value)) {
        return { ok: false, error: `${def.id} must be one of: ${Array.from(allowed).join(', ')}` }
      }
      return { ok: true, value }
    }
  }
}

/**
 * Resolve defaults (and drop non-applicable options) without validating types.
 * Prefer `validateOptions` for server-side usage.
 */
export function resolveOptions(
  defs: GameOptionDefinition[] | undefined,
  raw: GameOptionsValue | undefined,
  ctx: GameOptionsContext
): GameOptionsValue {
  const resolved: GameOptionsValue = {}
  const safeRaw = raw || {}
  for (const def of defs || []) {
    if (!appliesForPlayerCount(def, ctx)) continue
    const v = safeRaw[def.id]
    resolved[def.id] = v === undefined ? defaultFor(def, ctx) : v
  }
  return resolved
}

/**
 * Validate and resolve options. Unknown keys are ignored.
 * Invalid values fall back to defaults and produce errors.
 */
export function validateOptions(
  defs: GameOptionDefinition[] | undefined,
  raw: GameOptionsValue | undefined,
  ctx: GameOptionsContext
): ValidateOptionsResult {
  const errors: string[] = []
  const safeRaw = raw || {}
  const resolved: GameOptionsValue = {}

  for (const def of defs || []) {
    if (!appliesForPlayerCount(def, ctx)) continue

    const provided = safeRaw[def.id]
    if (provided === undefined) {
      resolved[def.id] = defaultFor(def, ctx)
      continue
    }

    const result = validateSingle(def, provided)
    if (result.ok) {
      resolved[def.id] = result.value
    } else {
      errors.push(result.error)
      resolved[def.id] = defaultFor(def, ctx)
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors, resolved }
  }

  return { ok: true, resolved }
}

/**
 * Compute effective options for a given player count from saved global/per-count buckets.
 */
export function effectiveOptionsForCount(
  defs: GameOptionDefinition[] | undefined,
  saved: GameSavedOptions | undefined,
  ctx: GameOptionsContext
): GameOptionsValue {
  const playerCount = playerCountBucket(ctx.playerCount)
  const global = saved?.global || {}
  const perCount = (typeof playerCount === 'number' ? saved?.perPlayerCount?.[playerCount] : undefined) || {}

  const merged: GameOptionsValue = {}

  for (const def of defs || []) {
    if (!appliesForPlayerCount(def, ctx)) continue

    const fromSaved =
      def.scope === 'global'
        ? global[def.id]
        : typeof playerCount === 'number'
          ? perCount[def.id]
          : undefined

    merged[def.id] = fromSaved === undefined ? defaultFor(def, ctx) : fromSaved
  }

  // Validate merged; fall back to defaults if saved values are invalid.
  const validated = validateOptions(defs, merged, ctx)
  return validated.resolved || merged
}

